import { useRef, type ReactNode } from 'react'
import { FlatList, View } from 'react-native'

import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'

import { useConversationsPreviewQuery } from '../../../services/useConversationsPreviewQuery'
import { ConversationPreview } from '../ConversationPreview'
import { UnreadConversationsToggle } from '../UnreadConversationsToggle'
import { ConversationPreviewPlaceholder } from './components/ConversationPreviewPlaceholder'

type TConversationPreviewListProps = {
  conversationId: number | undefined
  openedConversationId: number | undefined
}

export const ConversationPreviewList = ({
  conversationId,
  openedConversationId,
}: TConversationPreviewListProps): ReactNode => {
  const flatListRef = useRef<FlatList>(null)
  const scrollOffsetRef = useRef(0)
  const { height } = useDimensions()
  const {
    data: conversations,
    isPending,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    // We need onEndReached to be triggered on load when all the items of the first page are visible
    hasNextPage: _activateHasNextPageListener,
  } = useConversationsPreviewQuery(height > 950 ? 24 : undefined)

  return (
    <FlatList
      ref={flatListRef}
      testID={TEST_ID.CONVERSATION.PREVIEW.LIST}
      data={conversations}
      ListEmptyComponent={() =>
        isPending &&
        !conversations && (
          <>
            {Array.from({ length: 10 }, (_, i) => (
              <ConversationPreviewPlaceholder key={`placeholder-${i}`} />
            ))}
          </>
        )
      }
      ListHeaderComponent={() => (
        <View className="flex-row justify-end gap-2 pb-2">
          <UnreadConversationsToggle />
        </View>
      )}
      keyExtractor={(item) => `${item.id}`}
      contentContainerClassName="gap-1 py-4 px-2"
      renderItem={({ item }) => (
        <ConversationPreview
          conversation={item}
          isSelected={conversationId === item.id}
          openedConversationId={openedConversationId}
        />
      )}
      initialNumToRender={24}
      ListFooterComponent={
        isFetchingNextPage ? (
          <>
            {Array.from({ length: 3 }, (_, i) => (
              <ConversationPreviewPlaceholder key={`placeholder-${i}`} />
            ))}
          </>
        ) : null
      }
      onEndReached={() => {
        if (!isFetchingNextPage && !isError) fetchNextPage()
      }}
      onScroll={(event) => {
        scrollOffsetRef.current = event.nativeEvent.contentOffset.y
      }}
      scrollEventThrottle={50}
      onLayout={() => flatListRef.current?.scrollToOffset({ offset: scrollOffsetRef.current, animated: false })}
    />
  )
}
